import { Injectable } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SearchUKAddressResponse } from '../models/uk_address';
import { DatabaseService } from './base/database.service';

export interface StructuredAddress {
  address_line_1?: string | null;
  streetNumber?: string | null;
  streetName?: string | null;
  city?: string | null;
  neighborhood?: string | null;
  state?: string | null;
  county?: string | null;
  country?: string | null;
  postcode?: string | null;
  postalCode?: string | null;
  buildingName?: string | null;
  unitNumber?: string | null;
  address_validation?: AddressValidationPayload;
  [key: string]: unknown;
}

export interface AddressValidationConfig {
  enabled?: boolean;
  regionCode?: string;
  region_codes?: string[];
  pass?: Record<string, unknown>;
  flag?: Record<string, unknown>;
  pass_criteria?: Record<string, unknown>;
  flag_criteria?: Record<string, unknown>;
  maxRetries?: number;
  [key: string]: unknown;
}

export interface AddressValidationPayload {
  status: 'passed' | 'failed' | 'skipped' | 'user_confirmed' | string;
  possible_next_action?: string | null;
  validation_granularity?: string | null;
  flagged?: boolean;
  formatted_address?: string | null;
  response_id?: string | null;
  user_confirmed_override?: boolean;
  checked_at?: string | null;
  raw_response?: unknown;
  retry_count?: number;
  retry_limit_reached?: boolean;
  /** CPK-4239 fix: true when the caller (AGENT/ADMINISTRATOR) has no retry ceiling — lets the
   *  prompt offer the self-certify override even though retry_limit_reached never fires for them. */
  caller_unlimited_retries?: boolean;
  llm_suspicion?: {
    suspicious: boolean;
    confidence_score?: number;
    reasoning?: string;
    provider_used: 'bedrock' | 'none';
    checked_at: string;
  };
  [key: string]: unknown;
}

export interface AddressValidationDecision {
  status: 'passed' | 'failed' | 'skipped' | string;
  possible_next_action?: string | null;
  validation_granularity?: string | null;
  flagged?: boolean;
}

export interface AddressValidationResult {
  decision: AddressValidationDecision;
  payload: AddressValidationPayload;
}

@Injectable({
  providedIn: 'root',
})
export class AddressService extends DatabaseService<any> {
  constructor(private http: HttpClient) {
    super('cp_questionnaire_answer');
  }

  async searchUKAddress(searchValue: string): Promise<SearchUKAddressResponse> {
    // TODO: remove mock and test UK
    return Promise.resolve({
      Address: {
        data: [
          {
            _id: 'ab101ab',
            postcode: 'AB10 1AB',
            pafInfo: [
              {
                Thoroughfare: '',
                Street: 'Broad Street',
                'Double Dependent Locality': '',
                'Dependent Locality': '',
                'Post Town': 'ABERDEEN',
                Postcode: 'AB10 1AB',
                'Postcode Type': 'L',
                X_Coord: 394235,
                Y_Coord: 806529,
                Latitude: 57,
                Longitude: -2,
              },
            ],
            addtional_information: [
              {
                Postcode: 'AB10 1AB',
                'Postcode Sector': 'AB10 1',
                'Postcode District': 'AB10',
                'Postcode Area': 'AB',
                'Ward Code': 'S13002842',
                'Ward Name': 'George St/Harbour',
                'District Code': 'S12000033',
                'District Name': 'Aberdeen City',
                'Region Code': 'S99999999',
                'Region Name': 'Scotland',
                'County Code': '',
                'County Name': '',
                'Country Code': 'S92000003',
                'Country Name': 'Scotland',
                'LA Executive': 'NOC',
                Introduced: '201106',
                Retired: '',
              },
            ],
          },
          {
            _id: 'ab101af',
            postcode: 'AB10 1AF',
            pafInfo: [
              {
                Thoroughfare: '',
                Street: 'Broad Street',
                'Double Dependent Locality': '',
                'Dependent Locality': '',
                'Post Town': 'ABERDEEN',
                Postcode: 'AB10 1AF',
                'Postcode Type': 'L',
                X_Coord: 394181,
                Y_Coord: 806429,
                Latitude: 57,
                Longitude: -2,
              },
            ],
            addtional_information: [
              {
                Postcode: 'AB10 1AF',
                'Postcode Sector': 'AB10 1',
                'Postcode District': 'AB10',
                'Postcode Area': 'AB',
                'Ward Code': 'S13002842',
                'Ward Name': 'George St/Harbour',
                'District Code': 'S12000033',
                'District Name': 'Aberdeen City',
                'Region Code': 'S99999999',
                'Region Name': 'Scotland',
                'County Code': '',
                'County Name': '',
                'Country Code': 'S92000003',
                'Country Name': 'Scotland',
                'LA Executive': 'NOC',
                Introduced: '199606',
                Retired: '',
              },
            ],
          },
        ],
        error: null,
      },
    });

    const ret = (await firstValueFrom(
      this.http.get(
        environment.lambdaUrl +
          `/uk_address?pageSize=2&page=1&query=${searchValue}`,
        {
          headers: {
            apikey: environment.supabaseKey,
          },
        }
      )
    )) as SearchUKAddressResponse;

    return ret;
  }

  async validateAddress(
    userId: string,
    address: StructuredAddress,
    config?: AddressValidationConfig,
    previousResponseId?: string | null
  ): Promise<AddressValidationResult> {
    const token = (await this.supabase.auth.getSession()).data.session
      ?.access_token;

    return firstValueFrom(
      this.http.post<AddressValidationResult>(
        `${environment.lambdaUrl}/address_validation`,
        {
          userId,
          address,
          config,
          previousResponseId: previousResponseId || undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${token ?? ''}`,
          },
        }
      )
    );
  }
}
